import {
  ChevronDown,
  Mail,
  MapPin,
  Phone,
  RefreshCw,
  Star,
  User,
  UserPlus,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  formatCurrency,
  formatDateLong,
  MetadataRequestBadges,
  OrderCardUIProps,
  PaymentIcon,
  ProgressBar,
  SHIPPING_TYPE_OPTIONS,
  StorefrontLogo,
} from './order-shared';

export function OrderCardGrid({
  order,
  orderNum: _orderNum,
  shortId,
  storeName,
  customerName,
  customerEmail,
  customerPhone,
  billingLine,
  shippingLine,
  flagEmoji,
  salespersonName,
  paymentMethodStyle,
  paymentStatusStyle,
  flagInfo,
  progress,
  shippingType,
  isApproved,
  isStaff,
  noteOpen: _noteOpen,
  paymentOpen: _paymentOpen,
  salespersonOpen: _salespersonOpen,
  isSavingNote: _isSavingNote,
  onCardClick,
  onShippingTypeSelect,
  onNoteOpenChange,
  onPaymentOpenChange,
  onSalespersonOpenChange,
  onSaveNote: _onSaveNote,
}: OrderCardUIProps) {
  return (
    <div
      className="border-smoke bg-neutral-white flex cursor-pointer flex-col overflow-hidden rounded-2xl border shadow-sm transition-shadow hover:shadow-md"
      onClick={onCardClick}
    >
      {/* ── HEADER ── */}
      <div className="px-4 pt-4 pb-3">
        {/* Row 1: star + order num | flag + short id */}
        <div className="mb-3 flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-1.5">
            <Star size={14} className="fill-secondary-s500 text-secondary-s500 shrink-0" />
            <span className="truncate text-base font-semibold text-black">
              {order.orderNumber || 'N/A'}
            </span>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            {flagInfo && (
              <span className="text-base font-normal">
                Flag:{' '}
                <span className={`font-semibold ${flagInfo.color}`}>{flagInfo.label}</span>
              </span>
            )}
            <span className="text-lightGray text-base font-normal">{shortId}</span>
          </div>
        </div>

        {/* Row 2: store logo | $ badge */}
        <div className="flex items-center justify-between gap-2">
          <StorefrontLogo name={storeName} />
          <div
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border ${paymentStatusStyle.wrapper}`}
          >
            <span className={`text-base font-bold ${paymentStatusStyle.icon}`}>$</span>
          </div>
        </div>
      </div>

      {/* ── BODY ── */}
      <div className="flex flex-col gap-2.5 px-4 pb-4">
        {/* Row: Billing | Shipping | IP */}
        <div className="grid grid-cols-3 gap-1.5">
          <div className="border-smoke rounded-xl border px-2.5 py-2">
            <div className="mb-1 flex items-center gap-1">
              <span className="bg-info h-1.5 w-1.5 shrink-0 rounded-full" />
              <span className="text-lightGray text-[11px] leading-tight font-semibold tracking-wide uppercase">
                Billing Address
              </span>
            </div>
            <p className="truncate text-sm leading-snug font-normal text-black">{billingLine}</p>
          </div>
          <div className="border-smoke rounded-xl border px-2.5 py-2">
            <div className="mb-1 flex items-center gap-1">
              <span className="bg-danger h-1.5 w-1.5 shrink-0 rounded-full" />
              <span className="text-lightGray text-[11px] leading-tight font-semibold tracking-wide uppercase">
                Shipping Address
              </span>
            </div>
            <div className="flex items-start gap-1">
              {flagEmoji && <span className="shrink-0 text-sm leading-none">{flagEmoji}</span>}
              <p className="truncate text-sm leading-snug font-normal text-black">{shippingLine}</p>
            </div>
          </div>
          <div className="border-smoke rounded-xl border px-2.5 py-2">
            <div className="mb-1 flex items-center gap-1">
              <span className="bg-danger h-1.5 w-1.5 shrink-0 rounded-full" />
              <span className="text-lightGray text-[11px] leading-tight font-semibold tracking-wide uppercase">
                IP Address
              </span>
            </div>
            <p className="truncate text-sm leading-snug font-normal text-black">
              {order.ipAddress || 'N/A'}
            </p>
          </div>
        </div>

        {/* Row: Date & Time */}
        <div className="border-smoke rounded-xl border px-3 py-2.5">
          <div className="mb-1 flex items-center gap-1.5">
            <span className="bg-info h-2 w-2 shrink-0 rounded-full" />
            <span className="text-lightGray text-xs font-semibold tracking-wide uppercase">
              Order Creation Date & Time
            </span>
          </div>
          <p className="text-base text-black">{formatDateLong(order.createdAt)}</p>
        </div>

        {/* Row: Customer info | Shipping Type */}
        <div className="grid grid-cols-2 gap-1.5">
          {/* Customer info */}
          <div className="border-smoke space-y-2 rounded-xl border px-3 py-3">
            <div className="flex items-center gap-2">
              <User size={13} className="text-silver shrink-0" />
              <span className="truncate text-base text-black">{customerName}</span>
            </div>
            <div className="flex items-center gap-2">
              <Mail size={13} className="text-silver shrink-0" />
              <span className="text-lightGray truncate text-base">{customerEmail}</span>
            </div>
            <div className="flex items-center gap-2">
              <Phone size={13} className="text-silver shrink-0" />
              <span className="text-lightGray text-base">{customerPhone}</span>
            </div>
            <div className="flex items-start gap-2">
              <MapPin size={13} className="text-silver mt-0.5 shrink-0" />
              <span className="text-lightGray text-base leading-tight">{shippingLine}</span>
            </div>
          </div>

          {/* Shipping Type */}
          <div
            className="border-smoke flex flex-col gap-2 rounded-xl border px-3 py-3"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-lightGray text-xs font-semibold tracking-wide uppercase">
              Shipping Type
            </p>
            <div>
              <p className="text-lightGray mb-1 text-xs font-normal">Order Status</p>
              <ProgressBar value={progress.value} color={progress.color} />
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="border-smoke bg-neutral-white hover:bg-smoke1 flex w-full items-center justify-between rounded-xl border px-3 py-2 text-base font-normal text-black"
                >
                  <span className="truncate">{shippingType}</span>
                  <ChevronDown size={14} className="text-silver ml-1 shrink-0" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-52 max-h-64 overflow-y-auto">
                {SHIPPING_TYPE_OPTIONS.map((value) => (
                  <DropdownMenuItem
                    key={value}
                    onClick={() => onShippingTypeSelect(value)}
                    className={shippingType === value ? 'bg-primary-s50 text-primary-s500' : ''}
                  >
                    {value}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            {order.status === 'Cancellation Requested' && (
              <span className="rounded-full bg-danger px-3 py-0.5 text-xs font-semibold text-neutral-white w-fit">
                Cancellation Request
              </span>
            )}
            <MetadataRequestBadges metadata={order.metadata} />
          </div>
        </div>
      </div>

      {/* ── PAYMENT SECTION ── */}
      <div
        className="border-smoke mt-auto border-t px-4 pt-3 pb-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Row 1: payment method | salesperson box | marketplace badge */}
        <div className="mb-3 flex items-center gap-2">
          {/* Payment method */}
          <div className="flex min-w-0 shrink-0 items-center gap-2">
            {paymentMethodStyle ? (
              <>
                <PaymentIcon
                  iconName={paymentMethodStyle.iconName}
                  imageName={paymentMethodStyle.imageName}
                  lucideIcon={paymentMethodStyle.lucideIcon}
                  fallbackChar={paymentMethodStyle.label.charAt(0)}
                  iconColor={paymentMethodStyle.iconColor}
                  size={36}
                  wrapperClassName={`flex h-9 w-9 items-center justify-center rounded-full ${paymentMethodStyle.wrapper}`}
                />
                <span className="text-base font-semibold text-black">
                  {paymentMethodStyle.label}
                </span>
              </>
            ) : (
              <span className="text-sm font-normal text-lightGray">Not paid yet</span>
            )}
          </div>

          {/* Salesperson box (centered) */}
          <div className="flex flex-1 justify-center">
            <div className="border-smoke rounded-xl border px-4 py-2 text-center">
              <p className="text-base leading-tight font-semibold text-black">
                {salespersonName ?? 'N/A'}
              </p>
              {!isStaff && (
                <div className="mt-1.5 flex items-center justify-center gap-3">
                  {salespersonName ? (
                    <button
                      type="button"
                      title="Reassign Salesperson"
                      onClick={() => onSalespersonOpenChange(true)}
                      className="text-lightGray hover:text-black"
                    >
                      <RefreshCw size={15} />
                    </button>
                  ) : (
                    <button
                      type="button"
                      title="Assign Salesperson"
                      onClick={() => onSalespersonOpenChange(true)}
                      className="text-lightGray hover:text-black"
                    >
                      <UserPlus size={15} />
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Marketplace badge */}
          <span className="shrink-0 rounded-full bg-orange-100 px-3 py-0.5 text-xs font-semibold text-orange-500 uppercase">
            {order.marketplace || 'Caterpiller'}
          </span>
        </div>

        {/* Row 2: File Status + APPROVED | Amount */}
        <div className="mb-1 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="text-lightGray text-base">
              {Array.isArray(order.metadata?.salespersonFiles) && order.metadata.salespersonFiles.length > 0 ? 'Has Files' : 'No Files'}
            </span>
            {isApproved && (
              <span className="bg-success-light text-success rounded-full px-3 py-0.5 text-xs font-semibold">
                APPROVED
              </span>
            )}
          </div>
          <span className="shrink-0 text-2xl font-bold text-black">
            {formatCurrency(order.totalAmount)} USD
          </span>
        </div>

        {/* Row 3: More... | Revenue */}
        <div className="mb-4 flex items-center justify-between">
          <button
            type="button"
            onClick={() => onPaymentOpenChange(true)}
            className="text-primary-s500 text-base hover:underline"
          >
            More...
          </button>
          <p className="text-primary-s500 text-base font-normal">
            Revenue {formatCurrency(order.totalAmount)} USD
          </p>
        </div>

        {/* Notes */}
        {order.metadata?.notes && order.metadata.notes.length > 0 && (
          <div className="mb-3 flex flex-col gap-1 rounded-lg border border-smoke bg-smoke1 px-2.5 py-2">
            {order.metadata.notes.map((note) => (
              <p key={note.id} className="line-clamp-2 text-xs leading-snug text-lightGray">
                {note.text}
              </p>
            ))}
          </div>
        )}

        {/* Add Note */}
        <button
          type="button"
          onClick={() => onNoteOpenChange(true)}
          className="bg-primary-s50 text-primary-s500 w-full rounded-xl py-3 text-base font-normal transition-colors hover:bg-blue-100"
        >
          + Add Note
        </button>
      </div>
    </div>
  );
}
